<template>
  <div class="container-fluid mt-4">
    <div class="d-flex justify-content-between align-items-center mb-4">
      <h2>Gestión de Menús - Tabla Avanzada</h2>
      <div>
        <button @click="exportToExcel" class="btn btn-success me-2">
          <i class="bi bi-file-earmark-excel"></i> Exportar a Excel
        </button>
        <router-link to="/add-menu" class="btn btn-primary">
          <i class="bi bi-plus-circle"></i> Nuevo Menú
        </router-link>
      </div>
    </div>

    <div class="card">
      <div class="card-body">
        <!-- Vue Good Table Next -->
        <vue-good-table
          :columns="columns"
          :rows="menus"
          :search-options="{
            enabled: true,
            placeholder: 'Buscar menús...'
          }"
          :pagination-options="{
            enabled: true,
            perPage: 10,
            perPageDropdown: [5, 10, 15, 20],
            dropdownAllowAll: false,
            nextLabel: 'Siguiente',
            prevLabel: 'Anterior',
            rowsPerPageLabel: 'Filas por página',
            ofLabel: 'de',
            pageLabel: 'Página',
            allLabel: 'Todos'
          }"
          :sort-options="{
            enabled: true,
            initialSortBy: { field: 'Titulo', type: 'asc' }
          }"
          styleClass="vgt-table table-bordered table-striped"
          theme="nocturnal"
        >
          <template #table-row="props">
            <span v-if="props.column.field === 'actions'">
              <router-link :to="`/edit-menu/${props.row.Menu_id}`" class="btn btn-sm btn-info me-2">
                <i class="bi bi-pencil"></i>
              </router-link>
              <button @click="confirmDelete(props.row)" class="btn btn-sm btn-danger">
                <i class="bi bi-trash"></i>
              </button>
            </span>
            <span v-else-if="props.column.field === 'Es_activo'">
              <span :class="props.row.Es_activo ? 'badge bg-success' : 'badge bg-danger'">
                {{ props.row.Es_activo ? 'Activo' : 'Inactivo' }}
              </span>
            </span>
            <span v-else-if="props.column.field === 'Es_top'">
              <span :class="props.row.Es_top ? 'badge bg-primary' : 'badge bg-secondary'">
                {{ props.row.Es_top ? 'Sí' : 'No' }}
              </span>
            </span>
            <span v-else-if="props.column.field === 'Imagen'">
              <img v-if="props.row.Imagen" :src="props.row.Imagen" alt="Imagen" class="menu-image" />
              <span v-else>-</span>
            </span>
            <span v-else>
              {{ props.formattedRow[props.column.field] }}
            </span>
          </template>
        </vue-good-table>
      </div>
    </div>

    <!-- Modal de confirmación para eliminar -->
    <div class="modal fade" id="deleteModal" tabindex="-1" aria-labelledby="deleteModalLabel" aria-hidden="true">
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header bg-danger text-white">
            <h5 class="modal-title" id="deleteModalLabel">Confirmar eliminación</h5>
            <button type="button" class="btn-close" aria-label="Close" @click="closeModal"></button>
          </div>
          <div class="modal-body">
            <p>¿Estás seguro de que deseas eliminar este menú?</p>
            <p v-if="menuToDelete"><strong>Título:</strong> {{ menuToDelete.Titulo }}</p>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="closeModal">Cancelar</button>
            <button type="button" class="btn btn-danger" @click="deleteMenu">Eliminar</button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { useAccessControl } from '@/composables/useAccessControl'
import MenuTableView4Controller from '../controllers/MenuTableView4Ctrl.js';
import { VueGoodTable } from 'vue-good-table-next';
import 'vue-good-table-next/dist/vue-good-table-next.css';

export default {
  name: 'MenuTableView4',
  components: {
    VueGoodTable
  },
  setup() {
    const { requiresAuth, requiresMenuPermission } = useAccessControl({
      requiresAuth: true,
      requiresMenuPermission: true
    })

    return {
      requiresAuth,
      requiresMenuPermission
    }
  },
  data() {
    return {
      controller: new MenuTableView4Controller()
    };
  },
  computed: {
    menus() {
      return this.controller.menusList;
    },
    menuToDelete() {
      return this.controller.selectedMenuToDelete;
    },
    columns() {
      return this.controller.columns;
    }
  },
  async created() {
    await this.controller.initialize();
  },
  methods: {
    async fetchMenus() {
      await this.controller.loadMenus();
    },
    
    confirmDelete(menu) {
      this.controller.setMenuToDelete(menu);
      // Mostrar modal de confirmación usando DOM nativo
      const modalEl = document.getElementById('deleteModal');
      if (modalEl) {
        modalEl.classList.add('show');
        modalEl.style.display = 'block';
        document.body.classList.add('modal-open');
        const backdrop = document.createElement('div');
        backdrop.className = 'modal-backdrop fade show';
        document.body.appendChild(backdrop);
      }
    },
    
    closeModal() {
      const modalEl = document.getElementById('deleteModal');
      if (modalEl) {
        modalEl.classList.remove('show');
        modalEl.style.display = 'none';
        document.body.classList.remove('modal-open');
        const backdrop = document.querySelector('.modal-backdrop');
        if (backdrop) {
          document.body.removeChild(backdrop);
        }
      }
    },
    
    async deleteMenu() {
      try {
        await this.controller.deleteMenu();
        this.closeModal();
        this.$mgkAlert.success('Menú eliminado correctamente.');
      } catch (error) {
        this.$mgkAlert.error('Error al eliminar el menú. Por favor, intente de nuevo.');
      }
    },
    
    exportToExcel() {
      try {
        this.controller.exportToExcel();
        this.$mgkAlert.success('Datos exportados correctamente.');
      } catch (error) {
        this.$mgkAlert.error('Error al exportar los datos.');
      }
    }
  }
 };
</script>

<style scoped>
.menu-image {
  max-width: 50px;
  max-height: 50px;
  object-fit: contain;
}

/* Estilos para Vue Good Table */
:deep(.vgt-table) {
  border-collapse: collapse;
  width: 100%;
  font-size: 0.9rem;
}

:deep(.vgt-table th) {
  background-color: #f8f9fa;
  font-weight: bold;
  text-align: left;
  padding: 12px 8px;
}

:deep(.vgt-input, .vgt-select) {
  height: calc(1.5em + 0.75rem + 2px);
  padding: 0.375rem 0.75rem;
  font-size: 0.9rem;
  border: 1px solid #ced4da;
  border-radius: 0.25rem;
}

:deep(.vgt-wrap__footer) {
  padding: 1rem;
  background-color: #f8f9fa;
  border-top: 1px solid #dee2e6;
}

:deep(.vgt-wrap__footer .footer__row-count__label, .footer__row-count__select) {
  display: inline-block;
  margin-right: 10px;
}

:deep(.vgt-wrap__footer .footer__navigation__page-btn) {
  margin: 0 5px;
}

:deep(.vgt-wrap__footer .footer__navigation__info) {
  margin: 0 15px;
}
</style>
